import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getToken, setToken as storeToken, clearToken, migrateLegacyTokens } from '../utils/auth';

const ClinicAuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClinicAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => {
    // Run migration on first load
    migrateLegacyTokens();
    return getToken();
  });

  useEffect(() => {
    const currentToken = getToken();
    if (currentToken) {
      // Validate token and fetch user info
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    const currentToken = getToken();
    try {
      // Check if token is a clinic token by decoding it first
      let tokenPayload = null;
      try {
        tokenPayload = JSON.parse(atob(currentToken.split('.')[1]));
      } catch (e) {
        // Invalid token format - just clear local state, don't clear shared token
        setUser(null);
        setOrganization(null);
        setLoading(false);
        return;
      }
      
      // Only try clinic endpoint if this looks like a clinic token (has organization_id)
      if (!tokenPayload.organization_id) {
        // Not a clinic token - just clear local state without clearing the shared token
        setUser(null);
        setOrganization(null);
        setLoading(false);
        return;
      }
      
      // Get dashboard stats which includes user info
      const response = await axios.get(`${API_URL}/api/clinic/dashboard/stats`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setOrganization(response.data.organization);
      
      // Set user from decoded token
      setUser({
        id: tokenPayload.sub,
        email: tokenPayload.email,
        organization_id: tokenPayload.organization_id,
        role: tokenPayload.role
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch clinic user info:', error);
      // Only clear local clinic state, NOT the shared token
      // The token might be valid for education system
      setUser(null);
      setOrganization(null);
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/clinic/login`, {
        email,
        password
      });
      
      const { access_token, user: userData, organization: orgData } = response.data;
      
      storeToken(access_token);
      setTokenState(access_token);
      setUser(userData);
      setOrganization(orgData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (formData) => {
    try {
      const response = await axios.post(`${API_URL}/api/clinic/register`, formData);
      
      const { access_token, user: userData, organization: orgData } = response.data;
      
      storeToken(access_token);
      setTokenState(access_token);
      setUser(userData);
      setOrganization(orgData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    clearToken();
    setTokenState(null);
    setUser(null);
    setOrganization(null);
    setLoading(false);
  };

  // Refresh auth data (useful after payment to get updated subscription status)
  const refreshAuth = async () => {
    const currentToken = getToken();
    if (currentToken) {
      try {
        const response = await axios.get(`${API_URL}/api/clinic/dashboard/stats`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setOrganization(response.data.organization);
        return { success: true };
      } catch (error) {
        console.error('Failed to refresh auth:', error);
        return { success: false };
      }
    }
    return { success: false };
  };

  const isAuthenticated = !!getToken() && !!user;
  const hasActiveSubscription = organization?.subscription_status === 'active' || 
                                 organization?.subscription_status === 'trialing';

  return (
    <ClinicAuthContext.Provider
      value={{
        user,
        organization,
        token: getToken(),
        loading,
        isAuthenticated,
        hasActiveSubscription,
        login,
        register,
        logout,
        refreshAuth
      }}
    >
      {children}
    </ClinicAuthContext.Provider>
  );
};

export const useClinicAuth = () => {
  const context = useContext(ClinicAuthContext);
  if (!context) {
    throw new Error('useClinicAuth must be used within ClinicAuthProvider');
  }
  return context;
};
